import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { TimelineParkingEvent } from 'shared-lib';
import { ParkerPoolCapUsageDto, ParkingProcessService, ParkingRightService } from '../../../api/gen';

export interface PoolCapTimeline {
  events: TimelineParkingEvent[];
  truncated: boolean;
}

/**
 * Per-page cache for the car-limit panels. The Rights tab is torn down on every tab switch, so
 * without this each switch back would re-fetch the same cap and the same stays. Provided by the
 * license-plate page and cleared when it changes plate or refreshes.
 */
@Injectable()
export class ParkerPoolCapStore {
  /** Same cap as the org page's chart read: the curve needs the whole window, not one page. */
  static readonly TimelineFetchCap = 500;

  private readonly rightApi = inject(ParkingRightService);
  private readonly processApi = inject(ParkingProcessService);

  private readonly usageCache = new Map<string, Observable<ParkerPoolCapUsageDto | null>>();
  private readonly timelineCache = new Map<string, Observable<PoolCapTimeline | null>>();

  usage(poolParkingConfigId: string): Observable<ParkerPoolCapUsageDto | null> {
    let cached = this.usageCache.get(poolParkingConfigId);
    if (!cached) {
      cached = this.rightApi.parkingRightsParkerPoolCapPoolParkingConfigIdGet(poolParkingConfigId).pipe(
        catchError(() => of(null)),
        shareReplay(1)
      );
      this.usageCache.set(poolParkingConfigId, cached);
    }
    return cached;
  }

  /** Stays admitted on the cap in the window, pool-full ones included (the filter reads the fallback id too). */
  timeline(poolParkingConfigId: string, from: string, to: string): Observable<PoolCapTimeline | null> {
    const key = `${poolParkingConfigId}|${from}|${to}`;
    let cached = this.timelineCache.get(key);
    if (!cached) {
      cached = this.processApi.parkingProcessFilterEnrichedPost({
        poolParkingConfigIds: [poolParkingConfigId],
        startedFrom: new Date(from + 'T00:00:00').toISOString(),
        // End of the chosen day, not its midnight, or the last day drops out.
        startedTo: new Date(to + 'T23:59:59').toISOString(),
        pageIndex: 0,
        pageSize: ParkerPoolCapStore.TimelineFetchCap,
        includeDeleted: false,
        includeValidity: false
      }).pipe(
        map(result => ({
          events: (result.items ?? []).map(t => ({
            id: t.id ?? '',
            plate: t.licensePlateDetails?.fullNumber ?? undefined,
            startedAt: t.startedAt ?? '',
            endedAt: t.endedAt ?? null,
            // Pool-full: admitted short-term because the cap was already taken.
            isOverflow: !!t.fallbackFromPoolConfigId
          })).filter(e => !!e.startedAt),
          truncated: (result.totalCount ?? 0) > ParkerPoolCapStore.TimelineFetchCap
        })),
        catchError(() => of(null)),
        shareReplay(1)
      );
      this.timelineCache.set(key, cached);
    }
    return cached;
  }

  clear(): void {
    this.usageCache.clear();
    this.timelineCache.clear();
  }
}
